import { useState } from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { FormControl, FormLabel, IconButton, Stack, TextField } from '@mui/material';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import { useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import Divider from '@mui/material/Divider';
import { Button } from '@mui/material';
import StorageIcon from '@mui/icons-material/Storage';
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDInput from 'components/MDInput';

export default function EmbeddingDetails({ onSubmit, onReset }) {
  const [embeddingLoading, setEmbeddingLoading] = useState(false);

  const { formatMessage: msg } = useIntl();
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm({
    defaultValues: {
      metadata: [{ key: '', value: '' }],
      maxChunkCount: 10
    }
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'metadata'
  });

  const addMetadataField = () => {
    append({ key: '', value: '' });
  };

  const removeMetadataField = (index) => {
    remove(index);
  };

  const handleReset = () => {
    onReset();
    reset({ knowledgeBases: [], metadata: [{ key: '', value: '' }] });
  };

  function handleFormSubmit() {
    return (data) => {
      setEmbeddingLoading(true);
      onSubmit(data, handleReset, () => setEmbeddingLoading(false));
    };
  }

  return (
    <MDBox
      noValidate
      sx={{ display: 'flex', flex: 1, flexDirection: 'column' }}
      component="form"
      onSubmit={handleSubmit(handleFormSubmit())}
    >
      <Stack spacing={2} display="flex" direction="column" flex={1}>
        <Controller
          name="maxChunkCount"
          control={control}
          rules={{ required: msg({ id: 'field.chunk.count.required' }) }}
          render={({ field }) => (
            <MDInput
              required
              {...field}
              label={msg({ id: 'field.chunk.count.label' })}
              variant="filled"
              size="small"
              type="number"
            />
          )}
        />

        <FormControl>
          <FormLabel component="legend" id="metadata-label" sx={{ fontSize: 'medium', mb: 1 }}>
            {msg({ id: 'field.metadata.label' })}
          </FormLabel>
          {fields.map((item, index) => (
            <Stack direction="row" spacing={0} key={item.id} alignItems="center">
              <Controller
                name={`metadata[${index}].key`}
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={msg({ id: 'field.metadataKey.label' })}
                    variant="filled"
                    fullWidth
                    size="small"
                    error={!!errors?.metadata?.[index]?.key}
                    helperText={errors?.metadata?.[index]?.key && msg({ id: 'field.required' })}
                  />
                )}
              />
              <Divider orientation="vertical" />
              <Controller
                name={`metadata[${index}].value`}
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={msg({ id: 'field.metadataValue.label' })}
                    variant="filled"
                    fullWidth
                    size="small"
                    error={!!errors?.metadata?.[index]?.value}
                    helperText={errors?.metadata?.[index]?.value && msg({ id: 'field.required' })}
                  />
                )}
              />
              <IconButton
                size="large"
                color="error"
                onClick={() => removeMetadataField(index)}
                disabled={fields.length === 1}
              >
                <RemoveCircleIcon />
              </IconButton>
            </Stack>
          ))}
          <MDButton
            onClick={addMetadataField}
            variant="contained"
            color="primary"
            sx={{ mt: 2, width: 'fit-content' }}
          >
            {msg({ id: 'button.addMetadata' })}
          </MDButton>
        </FormControl>
      </Stack>
      <Divider sx={{ marginTop: 'auto' }} />
      <Stack direction="row" spacing={2} id="buttonsContainer" sx={{ height: '50px' }}>
        <MDButton type="button" fullWidth color="secondary" onClick={handleReset}>
          {msg({ id: 'button.reset' })}
        </MDButton>
        <Button
          type="submit"
          color="primary"
          fullWidth
          variant="contained"
          loading={embeddingLoading}
          loadingPosition="start"
          startIcon={<StorageIcon />}
          sx={{
            color: 'white !important',
            '&.Mui-disabled': {
              color: (theme) => `${theme.palette.secondary.main} !important`
            }
          }}
        >
          {msg({ id: embeddingLoading ? 'button.embed.loading' : 'button.embed' })}
        </Button>
      </Stack>
    </MDBox>
  );
}

EmbeddingDetails.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired
};
